/**
 * Reveal-on-scroll utility.
 *
 * Two flavours:
 *  - useReveal({ threshold, rootMargin }) → returns a ref. Adds the
 *    `is-revealed` class to the element the first time it intersects.
 *  - <Reveal> wrapper component for simple use:
 *      <Reveal delay={120}><h1>Hi</h1></Reveal>
 *
 * Pair with the `.reveal-on-scroll` CSS class (defined in App.css) for
 * the actual fade-up animation. Uses IntersectionObserver — single
 * one-shot reveal, no perf hit.
 */
import { useEffect, useRef } from "react";

export function useReveal(options = {}) {
  const ref = useRef(null);
  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") return;
    // Honour reduced-motion users — they get the final state immediately.
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      node.classList.add("is-revealed");
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            node.classList.add("is-revealed");
            obs.unobserve(node);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px", ...options },
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [options.threshold, options.rootMargin]);
  return ref;
}

export function Reveal({ children, delay = 0, as: Tag = "div", className = "", ...rest }) {
  const ref = useReveal();
  return (
    <Tag ref={ref} className={`reveal-on-scroll ${className}`} style={{ "--reveal-delay": `${delay}ms` }} {...rest}>
      {children}
    </Tag>
  );
}
